import { useState } from "react";
import { useToast } from "@/components/base/Toast";

type Step = 1 | 2 | 3;

interface Props {
  onClose: () => void;
  onConnected: () => void;
}

export function ExotelConnectModal({ onClose, onConnected }: Props) {
  const { showToast } = useToast();
  const [step, setStep] = useState<Step>(1);
  const [testNumber, setTestNumber] = useState("");
  const [calling, setCalling] = useState(false);

  const steps: { num: Step; label: string }[] = [
    { num: 1, label: "API Credentials" },
    { num: 2, label: "Configure" },
    { num: 3, label: "Test" },
  ];

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    showToast("Webhook URL copied to clipboard", "success");
  };

  const handleCallNow = () => {
    if (!testNumber.trim()) {
      showToast("Please enter a phone number", "error");
      return;
    }
    setCalling(true);
    setTimeout(() => {
      setCalling(false);
      showToast("Test call connected successfully. Duration: 0m 8s", "success");
    }, 1500);
  };

  const handleFinish = () => {
    showToast("Exotel connected successfully!", "success");
    onConnected();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-[#111827]">Connect Exotel</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 pt-5 pb-2">
          <div className="flex items-center gap-0">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                      step > s.num
                        ? "bg-green-500 text-white"
                        : step === s.num
                        ? "bg-indigo-600 text-white"
                        : "bg-gray-200 text-[#6b7280]"
                    }`}
                  >
                    {step > s.num ? <i className="ri-check-line" /> : s.num}
                  </div>
                  <span
                    className={`text-[10px] font-medium mt-1.5 whitespace-nowrap ${
                      step >= s.num ? "text-[#374151]" : "text-[#9ca3af]"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 -mt-5 mx-1 rounded-full transition-colors ${
                      step > s.num ? "bg-green-500" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-5">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Exotel SID
                </label>
                <input
                  type="text"
                  placeholder="EXTxxxxxxxx"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  API Key
                </label>
                <input
                  type="password"
                  placeholder="Enter API key"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  API Token
                </label>
                <input
                  type="password"
                  placeholder="Enter API token"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Subdomain
                </label>
                <input
                  type="text"
                  placeholder="your-company.exotel.in"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <a
                href="#"
                className="inline-block text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap"
              >
                Get credentials from Exotel dashboard →
              </a>
              <button
                onClick={() => setStep(2)}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Next
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Caller ID
                </label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option>+91-8041122334</option>
                  <option>+91-8041122335</option>
                  <option>+91-8041122336</option>
                </select>
              </div>

              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#374151]">Record calls</p>
                  <p className="text-xs text-[#6b7280] mt-0.5">Automatically record all calls</p>
                </div>
                <button
                  className="relative w-10 h-6 rounded-full bg-indigo-600 transition-colors"
                  onClick={() => showToast("Call recording toggled", "info")}
                >
                  <span className="absolute top-0.5 left-[18px] w-5 h-5 rounded-full bg-white shadow transition-transform" />
                </button>
              </div>

              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#374151]">Auto-log calls</p>
                  <p className="text-xs text-[#6b7280] mt-0.5">
                    Auto-create call log entry after each call
                  </p>
                </div>
                <button
                  className="relative w-10 h-6 rounded-full bg-indigo-600 transition-colors"
                  onClick={() => showToast("Auto-logging toggled", "info")}
                >
                  <span className="absolute top-0.5 left-[18px] w-5 h-5 rounded-full bg-white shadow transition-transform" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Webhook URL
                  <span className="text-[#9ca3af] ml-1 font-normal">
                    — Paste this in Exotel webhook settings
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value="https://api.crmpro.in/webhooks/exotel/prop-2024-a1b2"
                    readOnly
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-[#374151] font-mono"
                  />
                  <button
                    onClick={() =>
                      handleCopy("https://api.crmpro.in/webhooks/exotel/prop-2024-a1b2")
                    }
                    className="px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <button
                onClick={() => setStep(3)}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Next
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h4 className="text-sm font-semibold text-[#111827] mb-2">Make a test call</h4>
                <p className="text-xs text-[#6b7280] mb-3">
                  Enter a number to verify the Exotel integration works
                </p>
                <input
                  type="text"
                  value={testNumber}
                  onChange={(e) => setTestNumber(e.target.value)}
                  placeholder="+91-9876543210"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-3"
                />
                <button
                  onClick={handleCallNow}
                  disabled={calling}
                  className="w-full px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {calling ? "Calling..." : "Call Now"}
                </button>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-xs" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Test call connected successfully
                      </p>
                      <p className="text-xs text-green-700">Duration: 0m 8s</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleFinish}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}